import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { AlertCircle, Download, FileSpreadsheet, Loader2, Plus, Upload as UploadIcon, X } from 'lucide-react'
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import { Control, Controller, useFieldArray, useForm } from 'react-hook-form'

import { taxService } from '../../../services/inventory.service'
import { paymentMethodService, PaymentMethodItem } from '../../../services/payment-method.service'
import { PurchaseExcelPreview, purchasesService } from '../../../services/purchases.service'
import { supplierService, SupplierItem } from '../../../services/supplier.service'
import { CatalogSelect } from '../../ui/CatalogSelect'
import { FormError } from '../../ui/FormError'
import { SatKeySelect } from '../../ui/SatKeySelect'
import { Badge } from '../../ui/badge'
import { Button } from '../../ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog'
import { Input } from '../../ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select'

import {
  excelRowDiagnostic,
  ManualEntryFormValues,
  manualEntrySchema,
  mapExcelPreviewRow,
  purchaseItemBadgeLabel,
} from './manual-entry-form'

interface ManualEntryModalProps {
  showManualEntryModal: boolean
  setShowManualEntryModal: (show: boolean) => void
  onConfirm: (data: ManualEntryFormValues, excelFile?: File) => Promise<void>
}

interface TaxItem {
  id: number
  name: string
  percentage: string | number
}

interface CatalogSelectionInput {
  id?: string | number
  name?: string
  title?: string
  description?: string
  requires_lot?: boolean | number
}

function catalogSelection(value: unknown): { id: string | number; name: string; requires_lot?: boolean } | null {
  if (!value || typeof value !== 'object') return null

  const item = value as CatalogSelectionInput
  if (item.id === undefined || item.id === null || item.id === '') return null

  return {
    id: item.id,
    name: item.name || item.title || item.description || `ID: ${item.id}`,
    ...(item.requires_lot !== undefined ? { requires_lot: Boolean(item.requires_lot) } : {}),
  }
}

function selectedCatalogItem(value: string, item: { id: string | number; name: string; requires_lot?: boolean } | null | undefined) {
  return item && String(item.id) === String(value) ? item : undefined
}

function unwrapList<T>(response: unknown): T[] {
  if (Array.isArray(response)) return response
  if (!response || typeof response !== 'object' || !('data' in response)) return []
  const value = (response as { data?: unknown }).data
  if (Array.isArray(value)) return value
  if (value && typeof value === 'object' && 'data' in value) {
    const nested = (value as { data?: unknown }).data
    if (Array.isArray(nested)) return nested
  }
  return []
}

let manualRowSequence = 0

function emptyProduct() {
  manualRowSequence += 1
  return {
    origin: 'manual' as const,
    rowKey: `manual:${Date.now()}:${manualRowSequence}`,
    matchStatus: 'new_product' as const,
    previewErrors: [],
    previewWarnings: [],
    catalogSelections: {
      category: null,
      department: null,
      unit: null,
      productType: null,
    },
    name: '',
    description: '',
    barcode: '',
    satKeyCode: '',
    satKeyName: '',
    categoryId: '',
    departmentId: '',
    productTypeId: '',
    unitId: '',
    taxId: '',
    requiresLot: false,
    lotNumber: '',
    expiryDate: '',
  }
}

export function ManualEntryModal({ showManualEntryModal, setShowManualEntryModal, onConfirm }: ManualEntryModalProps) {
  const today = new Date().toISOString().slice(0, 10)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [excelFile, setExcelFile] = useState<File>()
  const [excelPreview, setExcelPreview] = useState<PurchaseExcelPreview>()
  const [excelError, setExcelError] = useState('')
  const [analyzingExcel, setAnalyzingExcel] = useState(false)
  const [downloadingTemplate, setDownloadingTemplate] = useState(false)

  const suppliersQuery = useQuery({
    queryKey: ['purchase-suppliers', 'active'],
    queryFn: () => supplierService.getItems({ all: true, is_active: true }),
    enabled: showManualEntryModal,
  })
  const paymentMethodsQuery = useQuery({
    queryKey: ['purchase-payment-methods', 'active'],
    queryFn: () => paymentMethodService.getItems({ all: true, is_active: true }),
    enabled: showManualEntryModal,
  })
  const taxesQuery = useQuery({
    queryKey: ['taxes', 'all'],
    queryFn: () => taxService.getTaxes({ all: true }),
    enabled: showManualEntryModal,
  })

  const suppliers = useMemo(() => unwrapList<SupplierItem>(suppliersQuery.data), [suppliersQuery.data])
  const paymentMethods = useMemo(() => unwrapList<PaymentMethodItem>(paymentMethodsQuery.data), [paymentMethodsQuery.data])
  const taxes = useMemo(() => unwrapList<TaxItem>(taxesQuery.data), [taxesQuery.data])

  const {
    register, handleSubmit, control, reset, setValue, watch, getValues,
    formState: { errors, isSubmitting, isValid },
  } = useForm<ManualEntryFormValues>({
    resolver: zodResolver(manualEntrySchema) as never,
    mode: 'onChange',
    defaultValues: {
      noteNumber: '', supplierId: '', purchaseDate: '', entryDate: today,
      paymentCondition: undefined, paymentMethodId: '', products: [],
    },
  })

  const { fields, append, remove, replace } = useFieldArray({ control, name: 'products' })
  const products = watch('products')

  useEffect(() => {
    if (!showManualEntryModal) return
    reset({
      noteNumber: '', supplierId: '', purchaseDate: '', entryDate: today,
      paymentCondition: undefined, paymentMethodId: '', products: [],
    })
    setExcelFile(undefined)
    setExcelPreview(undefined)
    setExcelError('')
  }, [reset, showManualEntryModal, today])

  const analyzeExcel = async (file: File) => {
    setAnalyzingExcel(true)
    setExcelError('')
    try {
      const response = await purchasesService.previewExcel(file)
      const manualRows = getValues('products').filter((product) => product.origin === 'manual')
      replace([...manualRows, ...response.data.rows.map(mapExcelPreviewRow)])
      setExcelFile(file)
      setExcelPreview(response.data)
    } catch (error) {
      const message = typeof error === 'object' && error && 'response' in error
        ? (error as { response?: { data?: { message?: string; errors?: { file?: string[] } } } }).response?.data
        : undefined
      const apiMessage = message?.errors?.file?.[0] || message?.message
      setExcelError(apiMessage && !apiMessage.startsWith('validation.')
        ? apiMessage
        : 'No fue posible leer el archivo. Verifica que sea la plantilla .xlsx de productos y vuelve a intentarlo.')
    } finally {
      setAnalyzingExcel(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleExcelFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) void analyzeExcel(file)
  }

  const downloadTemplate = async () => {
    setDownloadingTemplate(true)
    setExcelError('')
    try {
      const blob = await purchasesService.downloadExcelTemplate()
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = 'plantilla-importacion-productos.xlsx'
      anchor.click()
      URL.revokeObjectURL(url)
    } catch {
      setExcelError('No fue posible descargar la plantilla')
    } finally {
      setDownloadingTemplate(false)
    }
  }

  return (
    <Dialog open={showManualEntryModal} onOpenChange={setShowManualEntryModal}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle>Agregar Productos sin XML</DialogTitle>
          <DialogDescription>Registra una nota manualmente o carga productos con la plantilla Excel para la sucursal activa.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit((data) => onConfirm(data, excelFile))} className="space-y-6">
          <div className="grid grid-cols-1 gap-4 rounded-lg bg-slate-50 p-4 md:grid-cols-2">
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700">No. Nota <span className="text-red-600">*</span></label>
              <Input {...register('noteNumber')} placeholder="Ej: NOTA-001" />
              <FormError message={errors.noteNumber?.message} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700">Proveedor <span className="text-red-600">*</span></label>
              <Controller control={control} name="supplierId" render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange} disabled={suppliersQuery.isLoading || suppliersQuery.isError}>
                  <SelectTrigger><SelectValue placeholder={suppliersQuery.isLoading ? 'Cargando proveedores...' : 'Seleccionar proveedor'} /></SelectTrigger>
                  <SelectContent>{suppliers.map((supplier) => <SelectItem key={supplier.id} value={String(supplier.id)}>{supplier.name}</SelectItem>)}</SelectContent>
                </Select>
              )} />
              {suppliersQuery.isError && <FormError message="No fue posible cargar los proveedores activos" />}
              <FormError message={errors.supplierId?.message} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700">Fecha de Compra <span className="text-red-600">*</span></label>
              <Input type="date" max={today} {...register('purchaseDate')} />
              <FormError message={errors.purchaseDate?.message} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700">Fecha de Ingreso <span className="text-red-600">*</span></label>
              <Input type="date" {...register('entryDate')} readOnly className="bg-slate-100" />
              <FormError message={errors.entryDate?.message} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700">Forma de Pago <span className="text-red-600">*</span></label>
              <Controller control={control} name="paymentCondition" render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar forma de pago" /></SelectTrigger>
                  <SelectContent><SelectItem value="cash">Contado</SelectItem><SelectItem value="credit">Crédito</SelectItem></SelectContent>
                </Select>
              )} />
              <FormError message={errors.paymentCondition?.message} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700">Método de Pago <span className="text-red-600">*</span></label>
              <Controller control={control} name="paymentMethodId" render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange} disabled={paymentMethodsQuery.isLoading || paymentMethodsQuery.isError}>
                  <SelectTrigger><SelectValue placeholder={paymentMethodsQuery.isLoading ? 'Cargando métodos...' : 'Seleccionar método'} /></SelectTrigger>
                  <SelectContent>{paymentMethods.map((method) => <SelectItem key={method.id} value={String(method.id)}>{method.name}</SelectItem>)}</SelectContent>
                </Select>
              )} />
              {paymentMethodsQuery.isError && <FormError message="No fue posible cargar los métodos de pago activos" />}
              <FormError message={errors.paymentMethodId?.message} />
            </div>
          </div>

          <section className="space-y-3">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
              <h3 className="font-medium text-slate-900">Partidas de compra</h3>
              <div className="flex flex-wrap gap-2">
                <Button type="button" size="sm" variant="outline" onClick={() => append(emptyProduct())}>
                  <Plus className="mr-2 h-4 w-4" /> Agregar Producto
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={() => void downloadTemplate()} disabled={downloadingTemplate}>
                  {downloadingTemplate ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                  Descargar plantilla
                </Button>
                <input ref={fileInputRef} type="file" accept=".xlsx" onChange={handleExcelFile} className="hidden" />
                <Button type="button" size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={analyzingExcel}>
                  {analyzingExcel ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UploadIcon className="mr-2 h-4 w-4" />}
                  {excelFile ? 'Reemplazar Excel' : 'Subir Excel'}
                </Button>
              </div>
            </div>

            {excelFile && excelPreview && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                <div className="flex flex-wrap items-center gap-2 text-sm text-blue-900">
                  <FileSpreadsheet className="h-4 w-4" />
                  <span className="font-medium">{excelFile.name}</span>
                  <Badge variant="outline">{excelPreview.summary.total} filas</Badge>
                  <Badge className="bg-orange-100 text-orange-700">{excelPreview.summary.new_products} nuevos</Badge>
                  <Badge className="bg-blue-100 text-blue-700">{excelPreview.summary.existing_products} existentes</Badge>
                  {excelPreview.summary.incomplete > 0 && <Badge className="bg-yellow-100 text-yellow-700">{excelPreview.summary.incomplete} por completar</Badge>}
                  {excelPreview.summary.blocked > 0 && <Badge className="bg-red-100 text-red-700">{excelPreview.summary.blocked} bloqueados</Badge>}
                </div>
                <p className="mt-1 text-xs text-blue-700">Cantidad recibida desde: {excelPreview.file.stock_header}</p>
              </div>
            )}
            {excelError && <div className="flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />{excelError}</div>}
            <FormError message={errors.products?.root?.message ?? (!Array.isArray(errors.products) ? errors.products?.message : undefined)} />

            {fields.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-500">
                Agrega un producto manualmente o carga la plantilla Excel.
              </div>
            ) : (
              <div className="space-y-4">
                {fields.map((field, index) => {
                  const current = products?.[index]
                  const productErrors = errors.products?.[index]
                  const existing = current?.matchStatus === 'existing_product' ? current.existingProduct : undefined
                  const blocked = current?.matchStatus === 'blocked'
                  const tax = taxes.find((item) => String(item.id) === current?.taxId)
                  const basePrice = current?.cost !== undefined && current?.margin !== undefined
                    ? Number(current.cost) * (1 + Number(current.margin) / 100)
                    : null
                  const finalPrice = basePrice === null ? null : basePrice * (1 + Number(tax?.percentage ?? 0) / 100)

                  if (blocked) {
                    const rowDiagnostic = excelRowDiagnostic(current?.rowNumber)

                    return (
                      <article key={field.id} className="rounded-lg border-2 border-red-200 bg-red-50 p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div><Badge className="bg-red-100 text-red-700">{purchaseItemBadgeLabel('blocked', index)}</Badge><p className="mt-2 font-medium">{current?.name || current?.barcode || 'Fila sin código'}</p>{rowDiagnostic && <p className="mt-1 text-xs text-red-600">{rowDiagnostic}</p>}</div>
                          {current?.origin === 'manual' && <Button type="button" size="sm" variant="ghost" onClick={() => remove(index)} className="text-red-600"><X className="h-4 w-4" /></Button>}
                        </div>
                        <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-red-700">{current?.previewErrors.map((message) => <li key={message}>{message}</li>)}</ul>
                      </article>
                    )
                  }

                  if (existing) {
                    return (
                      <article key={field.id} className="overflow-hidden rounded-lg border-2 border-blue-200 bg-white">
                        <div className="flex items-start justify-between gap-3 bg-blue-50 p-4">
                          <div><Badge className="bg-blue-100 text-blue-700">{purchaseItemBadgeLabel('existing_product', index)}</Badge><p className="mt-2 font-semibold">{existing.name}</p><p className="text-xs text-slate-500">{existing.barcode} · Stock actual: {existing.currentStock}</p></div>
                          {current?.origin === 'manual' && <Button type="button" size="sm" variant="ghost" onClick={() => remove(index)} className="text-red-600"><X className="h-4 w-4" /></Button>}
                        </div>
                        <div className="grid grid-cols-2 gap-3 border-b p-4 text-sm md:grid-cols-4">
                          <div><span className="text-xs text-slate-500">Categoría</span><p>{existing.category || 'Sin capturar'}</p></div>
                          <div><span className="text-xs text-slate-500">Departamento</span><p>{existing.department || 'Sin capturar'}</p></div>
                          <div><span className="text-xs text-slate-500">Tipo</span><p>{existing.productType || 'Sin capturar'}</p></div>
                          <div><span className="text-xs text-slate-500">Unidad</span><p>{existing.unit || 'Sin capturar'}</p></div>
                          <div><span className="text-xs text-slate-500">Costo actual</span><p>{existing.currentCost === null ? 'Sin capturar' : `$${existing.currentCost}`}</p></div>
                          <div><span className="text-xs text-slate-500">Precio actual</span><p>{existing.currentPrice === null ? 'Sin capturar' : `$${existing.currentPrice}`}</p></div>
                          <div><span className="text-xs text-slate-500">Utilidad actual</span><p>{existing.currentMargin === null ? 'Sin capturar' : `${existing.currentMargin}%`}</p></div>
                          <div><span className="text-xs text-slate-500">Clave SAT</span><p>{existing.satKeyCode || 'Sin capturar'}</p></div>
                        </div>
                        <div className="grid grid-cols-1 gap-4 p-4 md:grid-cols-3">
                          <div className="space-y-1"><label className="text-sm font-medium">Cantidad recibida <span className="text-red-600">*</span></label><Input type="number" min="1" {...register(`products.${index}.quantity`)} placeholder="Cantidad" /><FormError message={productErrors?.quantity?.message} /></div>
                          <div className="space-y-1"><label className="text-sm font-medium">Costo sin impuesto</label><Input type="number" min="0" step="0.01" {...register(`products.${index}.cost`)} placeholder="Sin capturar" /></div>
                          <div className="space-y-1"><label className="text-sm font-medium">Impuesto</label><TaxSelect control={control} index={index} taxes={taxes} loading={taxesQuery.isLoading} /></div>
                          {current.requiresLot && <>
                            <div className="space-y-1"><label className="text-sm font-medium">Número de lote <span className="font-normal">(opcional)</span></label><Input {...register(`products.${index}.lotNumber`)} placeholder="Ej: LOTE-001" /><FormError message={productErrors?.lotNumber?.message} /></div>
                            <div className="space-y-1"><label className="text-sm font-medium">Caducidad <span className="font-normal">(opcional)</span></label><Input type="date" min={today} {...register(`products.${index}.expiryDate`)} /><FormError message={productErrors?.expiryDate?.message} /></div>
                          </>}
                        </div>
                        {current.previewWarnings.length > 0 && <Warnings messages={current.previewWarnings} />}
                      </article>
                    )
                  }

                  return (
                    <article key={field.id} className="rounded-lg border-2 border-orange-200 bg-white p-4">
                      <div className="mb-4 flex items-start justify-between gap-4">
                        <div className="flex-1 space-y-1"><div className="flex items-center gap-2"><Badge variant="outline" className="border-orange-300 bg-orange-100 text-orange-700">{purchaseItemBadgeLabel('new_product', index)}</Badge><Input {...register(`products.${index}.name`)} placeholder="Nombre del producto" /></div><FormError message={productErrors?.name?.message} /></div>
                        {current?.origin === 'manual' && <Button type="button" size="sm" variant="ghost" onClick={() => remove(index)} className="text-red-600"><X className="h-4 w-4" /></Button>}
                      </div>
                      <div className="rounded-md border border-slate-100 bg-slate-50 p-3">
                        <h4 className="mb-3 text-xs font-semibold uppercase text-slate-500">Datos del producto</h4>
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                          <div className="space-y-1 md:col-span-2"><label className="text-sm font-medium">Descripción <span className="text-red-600">*</span></label><Input {...register(`products.${index}.description`)} placeholder="Descripción del producto" /><FormError message={productErrors?.description?.message} /></div>
                          <div className="space-y-1"><label className="text-sm font-medium">Código de Barras <span className="text-red-600">*</span></label><Input {...register(`products.${index}.barcode`)} readOnly={current?.origin === 'excel'} className={current?.origin === 'excel' ? 'bg-slate-100' : ''} /><FormError message={productErrors?.barcode?.message} /></div>
                          <div className="space-y-1"><label className="text-sm font-medium">Clave SAT</label><SatKeySelect value={current?.satKeyCode} selectedName={current?.satKeyName} onChange={(code, name) => { setValue(`products.${index}.satKeyCode`, code, { shouldValidate: true }); setValue(`products.${index}.satKeyName`, name, { shouldValidate: true }) }} onClear={() => { setValue(`products.${index}.satKeyCode`, '', { shouldValidate: true }); setValue(`products.${index}.satKeyName`, '', { shouldValidate: true }) }} /><FormError message={productErrors?.satKeyCode?.message ?? productErrors?.satKeyName?.message} /></div>
                          {([['categoryId', 'Categoría', 'categories', 'category'], ['departmentId', 'Departamento', 'departments', 'department'], ['unitId', 'Tipo de Unidad', 'units', 'unit']] as const).map(([name, label, catalog, selectionKey]) => (
                            <div className="space-y-1" key={name}><label className="text-sm font-medium">{label} <span className="text-red-600">*</span></label><Controller control={control} name={`products.${index}.${name}`} render={({ field: catalogField }) => <CatalogSelect catalog={catalog} returnObject value={catalogField.value as string} selectedItem={selectedCatalogItem(String(catalogField.value ?? ''), current?.catalogSelections?.[selectionKey])} onChange={(value: unknown) => { const selected = catalogSelection(value); catalogField.onChange(selected ? String(selected.id) : ''); setValue(`products.${index}.catalogSelections.${selectionKey}`, selected, { shouldValidate: true }) }} />} /><FormError message={(productErrors as Record<string, { message?: string }> | undefined)?.[name]?.message} /></div>
                          ))}
                          <div className="space-y-1"><label className="text-sm font-medium">Tipo de Producto <span className="text-red-600">*</span></label><Controller control={control} name={`products.${index}.productTypeId`} render={({ field: typeField }) => <CatalogSelect catalog="product_types" returnObject value={typeField.value} selectedItem={selectedCatalogItem(String(typeField.value ?? ''), current?.catalogSelections?.productType)} onChange={(value: unknown) => { const item = value && typeof value === 'object' ? value as CatalogSelectionInput : null; const selected = catalogSelection(value); const requiresLot = Boolean(item?.requires_lot); typeField.onChange(selected ? String(selected.id) : ''); setValue(`products.${index}.catalogSelections.productType`, selected, { shouldValidate: true }); setValue(`products.${index}.requiresLot`, requiresLot, { shouldValidate: true }); if (!requiresLot) { setValue(`products.${index}.lotNumber`, '', { shouldValidate: true }); setValue(`products.${index}.expiryDate`, '', { shouldValidate: true }) } }} />} /><FormError message={productErrors?.productTypeId?.message} /></div>
                        </div>
                      </div>
                      <div className="mt-4 overflow-hidden rounded-md border border-emerald-100">
                        <div className="border-b border-emerald-100 bg-emerald-50/50 p-4"><p className="mb-4 text-xs text-emerald-700">Cantidad, costo, impuesto y utilidad son opcionales.</p><div className="grid grid-cols-1 gap-4 md:grid-cols-3"><div className="space-y-1"><label className="text-sm font-medium">Cantidad</label><Input type="number" min="1" {...register(`products.${index}.quantity`)} placeholder="Sin capturar" /></div><div className="space-y-1"><label className="text-sm font-medium">Costo sin Impuesto</label><Input type="number" min="0" step="0.01" {...register(`products.${index}.cost`)} placeholder="Sin capturar" /></div><div className="space-y-1"><label className="text-sm font-medium">Impuesto</label><TaxSelect control={control} index={index} taxes={taxes} loading={taxesQuery.isLoading} /></div></div></div>
                        <div className="grid grid-cols-1 gap-4 bg-slate-50 p-4 md:grid-cols-3"><div className="space-y-1"><label className="text-sm font-medium">Porcentaje de Utilidad</label><Input type="number" min="0" step="0.01" {...register(`products.${index}.margin`)} placeholder="Sin capturar" /></div><div><label className="text-sm font-medium">Precio Venta Base</label><div className="mt-1 flex h-10 items-center rounded-md border bg-white px-3 font-semibold">{basePrice === null ? 'Sin capturar' : `$${basePrice.toFixed(2)}`}</div></div><div><label className="text-sm font-medium text-emerald-800">Precio Final</label><div className="mt-1 flex h-10 items-center rounded-md border border-emerald-200 bg-emerald-50 px-3 font-bold text-emerald-700">{finalPrice === null || !current?.taxId ? 'Sin capturar' : `$${finalPrice.toFixed(2)}`}</div></div></div>
                        {current?.requiresLot && <div className="grid grid-cols-1 gap-4 border-t border-yellow-200 bg-yellow-50 p-4 md:grid-cols-2"><div className="space-y-1"><label className="text-sm font-medium text-yellow-900">Número de Lote <span className="font-normal">(opcional)</span></label><Input {...register(`products.${index}.lotNumber`)} /><FormError message={productErrors?.lotNumber?.message} /></div><div className="space-y-1"><label className="text-sm font-medium text-yellow-900">Fecha de Caducidad <span className="font-normal">(opcional)</span></label><Input type="date" min={today} {...register(`products.${index}.expiryDate`)} /><FormError message={productErrors?.expiryDate?.message} /></div></div>}
                      </div>
                      {current?.previewWarnings.length ? <Warnings messages={current.previewWarnings} /> : null}
                    </article>
                  )
                })}
              </div>
            )}
          </section>

          <div className="rounded-lg border border-primary/20 bg-primary/10 p-4 text-sm text-primary">Los productos nuevos sin precio completo quedarán inactivos. Los productos existentes conservarán su precio actual y quedarán pendientes para Ajustes de Precio.</div>
          <DialogFooter><Button type="button" variant="outline" onClick={() => setShowManualEntryModal(false)} disabled={isSubmitting}>Cancelar</Button><Button type="submit" disabled={!isValid || isSubmitting || analyzingExcel || suppliersQuery.isLoading || paymentMethodsQuery.isLoading || Boolean(excelPreview?.summary.blocked)}>{isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Confirmar Ingreso</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function TaxSelect({ control, index, taxes, loading }: { control: Control<ManualEntryFormValues>; index: number; taxes: TaxItem[]; loading: boolean }) {
  return (
    <Controller control={control} name={`products.${index}.taxId`} render={({ field }) => (
      <Select value={field.value || undefined} onValueChange={field.onChange}><SelectTrigger className="bg-white"><SelectValue placeholder={loading ? 'Cargando...' : 'Sin capturar'} /></SelectTrigger><SelectContent>{taxes.map((item) => <SelectItem key={item.id} value={String(item.id)}>{item.name} ({item.percentage}%)</SelectItem>)}</SelectContent></Select>
    )} />
  )
}

function Warnings({ messages }: { messages: string[] }) {
  return <div className="border-t border-yellow-200 bg-yellow-50 p-3 text-xs text-yellow-800"><ul className="list-disc space-y-1 pl-4">{messages.map((message) => <li key={message}>{message}</li>)}</ul></div>
}
